import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { DesignPackageServices } from "@/services/bedrock/DesignPackageServices";
import { DesignPackageDetail, PackageStatus } from "@/services/bedrock/dtos/DesignPackage";
import { designKeys } from "@/services/bedrock/queryKeys";
import { DesignPackageProgress, useDesignPackageProgress } from "@/utils/actioncable";
import { isInFlightStatus } from "../packageStatus";

const IN_FLIGHT_PACKAGE_STATUSES: string[] = ["queued", "generating_images", "generating_html", "rendering_previews"];

/**
 * Package-level cable progress over the cached detail. Only an in-flight detail takes the cable
 * status, so a stale progress entry can never pull a finished package back into Generating.
 */
export function withProgress(
  pkg: DesignPackageDetail | undefined,
  progress: DesignPackageProgress | undefined
): DesignPackageDetail | undefined {
  if (!pkg || !progress || progress.designPackageItemId != null) return pkg;
  if (!isInFlightStatus(pkg.status)) return pkg;
  const status = IN_FLIGHT_PACKAGE_STATUSES.includes(progress.status) ? (progress.status as PackageStatus) : pkg.status;
  return {
    ...pkg,
    status,
    progressStatus: progress.progressStatus ?? pkg.progressStatus,
    percent: progress.percent ?? pkg.percent,
  };
}

export function useDesignPackage(id: number | null) {
  const query = useQuery({
    queryKey: designKeys.detail(id ?? 0),
    queryFn: () => DesignPackageServices.get(id as number),
    enabled: id != null,
    staleTime: 15_000,
    retry: false,
  });
  const progress = useDesignPackageProgress(id);
  const pkg = useMemo(() => withProgress(query.data, progress), [query.data, progress]);

  return { ...query, pkg, progress };
}
